import { isEmpty, isNil } from 'lodash';
import { CSSProperties, ReactNode, useCallback, useState } from 'react';
import { MBButton } from '@shared/components/MBButton';
import { useDebouncedScrollTextInputIntoView } from '@shared/components/blocks/lib/inputTodo/hooks';
import { MBIconV2Name } from '@shared/components/image';
import { MBAutosizeTextInput } from '@shared/components/textInput';
import { MBTextBody1 } from '@shared/components/typography';
import { TextField } from '@shared/data/entity/fields/TextField';
import { useComponentFocus } from '@shared/scenes/cards/form/components/ComponentFocus';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import {
  getComponentIcon,
  getComponentPlaceholder,
  isTextComponent,
} from '@shared/scenes/workflows/utils';
import { grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { EditFieldContainer } from './EditFieldContainer';

interface Props {
  field: TextField | null;
  component: API.TextComponent | API.RelatedCardLookupComponent;
  updateField?: (componentId: string, field: TextField | null) => void;
  error?: string;
  allowClear?: boolean;
  icon?: MBIconV2Name;
  showIcon?: boolean;
  label?: string;
  showLabel?: boolean;
  indentLabel?: boolean;
  reserveIconSpace?: boolean;
  compactTop?: boolean;
  compactBottom?: boolean;
  subfooter?: ReactNode;
  isGrayScale?: boolean;
  isItalic?: boolean;
  style?: CSSProperties;
}

// fits above keyboard on mobile
const MAX_ROWS = 15;

// balancing these is not an exact science
// the second const decides whether we truncate a read only field
// the first determines by how much we truncate it.
// should err on the side of more than enough characters to ensure that amount of text
// is more than the row count would show.
const MAX_ROWS_READ_MORE = 6;
const MAX_READ_MORE_CHARACTERS = 500;

export function TextEditField({
  field,
  component,
  updateField,
  label,
  error,
  allowClear,
  icon,
  showIcon,
  showLabel,
  indentLabel,
  reserveIconSpace,
  compactTop,
  compactBottom,
  subfooter,
  isGrayScale,
  isItalic,
  style,
}: Props) {
  const responsiveForms = useResponsiveFormContext();
  const placeholder = getComponentPlaceholder(component);
  const debouncedScrollIntoView = useDebouncedScrollTextInputIntoView();
  const [hasInput, onInput, onClearInput] = useBoolean(false);
  const [isFocused, onFocus, onBlur] = useBoolean(false);
  const { setComponentFocused, setComponentBlurred } = useComponentFocus();
  const showLocalError = !isFocused;
  const showError = (!hasInput || showLocalError) && !isEmpty(error);
  const hasValue = !isEmpty(field?.value);
  const isMultiline = isTextComponent(component) ? !!component.multiline : !!field?.multiline;
  const isReadOnly = component.readonly || isNil(updateField);
  const showClearButton = hasValue && !isReadOnly && allowClear;
  const iconToUse = icon ?? getComponentIcon(component);
  const valueLength = field?.value.length ?? 0;
  const [isShowingReadMoreText, setIsShowingReadMoreText] = useState(false);
  const textIsTooLong = valueLength > MAX_READ_MORE_CHARACTERS;
  const showReadMoreButton = textIsTooLong && isReadOnly;
  const showAllText = isShowingReadMoreText && isReadOnly;

  const handleFocus = useCallback(() => {
    onFocus();
    setComponentFocused();
  }, [setComponentFocused, onFocus]);

  const handleBlur = useCallback(() => {
    onBlur();
    setComponentBlurred();
  }, [setComponentBlurred, onBlur]);

  const updateText = useCallback(
    (textValue: string) => {
      if (isEmpty(textValue)) {
        onClearInput();
      } else {
        onInput();
      }
      updateField?.(
        component.id,
        !isEmpty(textValue) ? new TextField(textValue, { multiline: isMultiline }) : null,
      );
    },
    [updateField, component.id, isMultiline, onClearInput, onInput],
  );

  const onClear = useCallback(() => {
    onClearInput();
    updateField?.(component.id, null);
  }, [updateField, component.id, onClearInput]);

  const maxRowsWhenMaxApplied = isReadOnly ? MAX_ROWS_READ_MORE : MAX_ROWS;
  const maxRows = showAllText ? undefined : maxRowsWhenMaxApplied;
  const textToShow =
    isShowingReadMoreText || !isReadOnly
      ? field?.value
      : field?.value.slice(0, MAX_READ_MORE_CHARACTERS);

  const unborderedInputStyle = { padding: 0 };
  const borderedInputStyle = {
    padding: 0,
    paddingTop: grid(1.25),
    paddingBottom: grid(1.25),
  };
  const inputStyle = responsiveForms ? borderedInputStyle : unborderedInputStyle;

  return (
    <EditFieldContainer
      component={component}
      icon={iconToUse}
      showIcon={showIcon}
      label={label}
      showLabel={!isNil(showLabel) ? showLabel : responsiveForms || hasValue}
      indentLabel={indentLabel}
      reserveIconSpace={reserveIconSpace}
      showClearButton={showClearButton}
      onClear={onClear}
      error={showError ? error : undefined}
      compactTop={compactTop}
      compactBottom={compactBottom}
      subfooter={subfooter}
      includeWrapperPadding={false}
      isGrayScale={isGrayScale}
      isItalic={isItalic}
      style={style}
      printContent={textToShow}
    >
      {(!isReadOnly || !hasValue) && (
        <MBAutosizeTextInput
          value={textToShow ?? ''}
          onChangeText={updateText}
          placeholder={placeholder}
          onFocus={() => {
            debouncedScrollIntoView();
            handleFocus();
          }}
          onBlur={handleBlur}
          style={inputStyle}
          onContentSizeChange={debouncedScrollIntoView}
          maxRows={maxRows}
          disabled={isReadOnly}
        />
      )}
      {isReadOnly && hasValue && (
        <MBTextBody1
          numberOfLines={isShowingReadMoreText ? undefined : MAX_ROWS_READ_MORE}
          selectable
          style={{
            paddingTop: responsiveForms ? grid(1.25) : undefined,
            paddingBottom: responsiveForms ? grid(1.25) : undefined,
            paddingRight: responsiveForms ? grid(2) : undefined,
          }}
        >
          {textToShow}
        </MBTextBody1>
      )}
      {showReadMoreButton && (
        <MBButton
          theme="transparent"
          size="small"
          onPress={() => setIsShowingReadMoreText(!isShowingReadMoreText)}
          title={isShowingReadMoreText ? i18n('card.list.showLess') : i18n('card.list.showMore')}
          style={{ padding: 0 }}
        />
      )}
    </EditFieldContainer>
  );
}
